export const DEFAULT_FREQUENCY_DAYS = 14;
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Epoch anchor: Monday 2024-01-01 — legacy / migration only. */
export const EPOCH_ANCHOR_MS = Date.UTC(2024, 0, 1);

export interface PulsePeriod {
  periodStart: string;
  periodEnd: string;
}

export function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDaysISO(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return toISODate(d);
}

export function getPeriodForDate(
  date: Date = new Date(),
  frequencyDays: number = DEFAULT_FREQUENCY_DAYS,
  anchorMs: number = EPOCH_ANCHOR_MS,
): PulsePeriod {
  const periodLengthMs = frequencyDays * MS_PER_DAY;
  const ms = date.getTime() - anchorMs;
  const periodIndex = Math.floor(ms / periodLengthMs);
  const startMs =
    periodIndex < 0 ? anchorMs : anchorMs + periodIndex * periodLengthMs;
  const endMs = startMs + periodLengthMs;
  return {
    periodStart: toISODate(new Date(startMs)),
    periodEnd: toISODate(new Date(endMs)),
  };
}

function shiftPeriodBack(
  period: PulsePeriod,
  frequencyDays: number,
): PulsePeriod {
  const startMs = Date.parse(`${period.periodStart}T00:00:00.000Z`);
  const lengthMs = frequencyDays * MS_PER_DAY;
  return {
    periodStart: toISODate(new Date(startMs - lengthMs)),
    periodEnd: period.periodStart,
  };
}

export function getCheckInPeriodForDate(
  date: Date = new Date(),
  frequencyDays: number = DEFAULT_FREQUENCY_DAYS,
  anchorMs: number = EPOCH_ANCHOR_MS,
): PulsePeriod {
  const current = getPeriodForDate(date, frequencyDays, anchorMs);
  const currentEndMs = Date.parse(`${current.periodEnd}T00:00:00.000Z`);
  if (date.getTime() < currentEndMs) {
    return shiftPeriodBack(current, frequencyDays);
  }
  return current;
}

export function getRecentPeriods(
  count: number,
  date: Date = new Date(),
  frequencyDays: number = DEFAULT_FREQUENCY_DAYS,
  anchorMs: number = EPOCH_ANCHOR_MS,
): PulsePeriod[] {
  const periodLengthMs = frequencyDays * MS_PER_DAY;
  const ms = date.getTime() - anchorMs;
  const periodIndex = Math.floor(ms / periodLengthMs);
  const currentStartMs =
    periodIndex < 0 ? anchorMs : anchorMs + periodIndex * periodLengthMs;

  const periods: PulsePeriod[] = [];
  for (let i = 0; i < count; i++) {
    const startMs = currentStartMs - i * periodLengthMs;
    const endMs = startMs + periodLengthMs;
    periods.push({
      periodStart: toISODate(new Date(startMs)),
      periodEnd: toISODate(new Date(endMs)),
    });
  }
  return periods;
}

export function isCheckInBoundary(
  date: Date,
  frequencyDays: number = DEFAULT_FREQUENCY_DAYS,
  anchorMs: number = EPOCH_ANCHOR_MS,
): boolean {
  const period = getPeriodForDate(date, frequencyDays, anchorMs);
  return toISODate(date) === period.periodStart;
}
